import React from 'react';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import 'focus-visible/dist/focus-visible';
import 'date-time-format-timezone';

import './styles.css';
import './responsive.css';
import { translationMessages } from './i18n';
import { setupMountNode } from './utils/modals';
import MainContainer from './containers/MainContainer';
import {
  PERMISSIONS_PROSPECTIVE_COLLEGE,
  PARENT_PERMISSIONS_PROSPECTIVE_COLLEGE,
  HUBS_ROOT_NODE_CLASS_NAME,
  PERMISSIONS_PARENT,
} from './constants';
import HostContextProvider from './context/host/provider';

/** Required configuration for all root UI components, unless [config.configure()](./config.js) is called,
 *  in which case no configuration needs to be provided to the UI component itself. */
export const configPropTypes = {
  session: PropTypes.object,
  scid: PropTypes.number,
  apiUrl: PropTypes.string,
  fcService: PropTypes.object,
  locale: PropTypes.string,
  origin: PropTypes.string,
  isEditMode: PropTypes.bool,
  Footer: PropTypes.any,
};

export default class CollegeProfileApp extends React.PureComponent {
  static propTypes = {
    children: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.arrayOf(PropTypes.element),
    ]),
    ...configPropTypes,
  };

  state = {
    userInfo: {},
    highschoolCollegeInfo: {},
    currentContextRef: null,
  };

  constructor(props) {
    super(props);
    console.log(
      'Starting CollegeProfile UI @ %s',
      process.env.REACT_APP_GIT_COMMIT /* istanbul ignore next */
        ? `${process.env.REACT_APP_GIT_COMMIT} / ${process.env.REACT_APP_GIT_BRANCH}`
        : 'development',
    ); // eslint-disable-line no-console
    this.contextRef = React.createRef();
  }

  initializeFavorite = (applyingTo, thinkingAbout) => {
    const { scid, fcService, session } = this.props;
    if (session.authType === 'Bearer' && !fcService.isCamino) {
      return {};
    }

    let isFavorite = false;
    let isApplyingTo = false;

    /* istanbul ignore next */
    if (thinkingAbout && Object.values(thinkingAbout).length >= 0) {
      isFavorite = !!Object.values(thinkingAbout).find(
        (college) => college.hobsonsId === scid,
      );
    }

    /* istanbul ignore next */
    if (applyingTo && Object.values(applyingTo).length >= 0) {
      isApplyingTo = !!Object.values(applyingTo).find(
        (college) => college.hobsonsId === scid,
      );
      isFavorite = isApplyingTo || isFavorite;
    }

    fcService.initFavorite(isFavorite);
    return { isFavorite, isApplyingTo };
  };

  canEditProspectiveApplication = (isFavorite) => {
    const { permissions = {}, userData } = this.state.userInfo;
    const userType = get(userData, 'userType');

    if (userType === PERMISSIONS_PARENT) {
      return (
        !isFavorite &&
        !!permissions.parent_edit &&
        permissions.parent_edit.includes(PARENT_PERMISSIONS_PROSPECTIVE_COLLEGE)
      );
    }

    return (
      !!permissions.edit /* istanbul ignore next */ &&
      permissions.edit.includes(PERMISSIONS_PROSPECTIVE_COLLEGE)
    );
  };

  toggleFavorite = (favStatus) => {
    /* istanbul ignore next */
    const { fcService = {} } = this.props;
    /* istanbul ignore else */
    if (favStatus === true) {
      fcService.favorite(`${this.props.scid}`);
    } else if (favStatus === false) {
      fcService.unfavorite(`${this.props.scid}`);
    }
  };

  navigateToNewCollegeProfile = (hiid) => {
    this.props.fcService.getUserInfo().then(() => {
      this.props.fcService.navigateTo(`/explore/college/${hiid}`);
    });
  };

  getOtherData() {
    if (isEmpty(this.state.userInfo)) {
      this.props.fcService.getUserInfo().then((userInfo) => {
        this.setState({ userInfo });
      });
    }
    this.props.fcService.getUserInfo().then((userInfo) => {
      if (!userInfo?.featureFlags?.featureNavianceStudentCollegeProfileAllSettled) {
        /* istanbul ignore next */
        if (isEmpty(this.state.highschoolCollegeInfo)) {
          /* istanbul ignore next */
          this.props.fcService
            .getHighschoolCollegeInfo()
            .then((highschoolCollegeInfo) => {
              /* istanbul ignore next */
              this.setState({ highschoolCollegeInfo });
            });
        }
      }
    });
  }

  componentDidUpdate() {
    this.getOtherData();
    this.setCurrentContextRef();
  }

  componentDidMount() {
    this.cleanupMountNode = setupMountNode();
    this.cleanupMountNodeAlt = setupMountNode('alt');

    /* istanbul ignore else */
    if (this.props.fcService) {
      this.getOtherData();
    }
    this.setCurrentContextRef();
  }

  componentWillUnmount() {
    this.cleanupMountNode();
    this.cleanupMountNodeAlt();
  }

  setCurrentContextRef = debounce(() => {
    const currentContextRef = get(this.contextRef, 'current', null);
    if (!this.state.currentContextRef && currentContextRef) {
      this.setState({
        currentContextRef,
      });
    }
  }, 500);

  /* istanbul ignore next */
  sendEmail = (payload) => {
    return this.props.fcService.sendEmail(payload);
  };

  render() {
    /* istanbul ignore next */
    const {
      session,
      scid,
      apiUrl,
      locale = 'en',
      origin,
      isEditMode,
      editData,
      Footer,
      fcService: {
        fetchCollegeEvents,
        visits,
        clickTrackingEvents,
        fetchCounselorComments,
      },
    } = this.props;
    const {
      trackClick,
      trackView,
      clickTrackingScholarships,
      uuid,
      getUrls,
      isCamino,
      isHE,
      headerMessage,
      connectToCollege,
      addToTransferPlan,
      topAreasOfStudyTitle,
    } = {
      uuid: get(this.props.fcService, 'collegeId', null),
      trackClick: get(this.props.fcService, 'trackClick', noop),
      trackView: get(this.props.fcService, 'trackView', noop),
      clickTrackingScholarships: get(
        this.props.fcService,
        'clickTrackingScholarships',
        noop,
      ),
      isCamino: get(this.props.fcService, 'isCamino', false),
      isHE: get(this.props.fcService, 'isHE', false),
      connectToCollege: get(this.props.fcService, 'connectToCollege', false),
      headerMessage: get(this.props.fcService, 'headerMessage'),
      topAreasOfStudyTitle: get(this.props.fcService, 'topAreasOfStudyTitle'),
      getUrls: get(this.props.fcService, 'getUrls', noop),
      addToTransferPlan: get(this.props.fcService, 'addToTransferPlan', noop),
    };
    let highschoolCollegeInfo = this.state.highschoolCollegeInfo;
    if (
      this.state.userInfo?.featureFlags?.featureNavianceStudentCollegeProfileAllSettled
    ) {
      highschoolCollegeInfo = this.props.fcService.getHighschoolCollegeInfo();
    }
    const {
      userInfo: { highSchool, userData, permissions, featureFlags },
      currentContextRef,
    } = this.state;
    const {
      collegeContact,
      deliveryType,
      applicationStatistics,
      applyingTo,
      thinkingAbout,
    } = this.props.fcService.getStudentCollegeInfo();
    const { isFavorite, isApplyingTo } = this.initializeFavorite(
      applyingTo,
      thinkingAbout,
    );
    const messages =
      translationMessages[locale] /* istanbul ignore next */ ||
      translationMessages[locale.split('-')[0]];

    return (
      <HostContextProvider
        trackClick={trackClick}
        trackView={trackView}
        clickTrackingScholarships={clickTrackingScholarships}
        uuid={uuid}
        scid={scid}
        getUrls={getUrls}
        isCamino={isCamino}
        isHE={isHE}
        connectToCollege={connectToCollege}
        headerMessage={headerMessage}
        addToTransferPlan={addToTransferPlan}
        topAreasOfStudyTitle={topAreasOfStudyTitle}
      >
        <div className={HUBS_ROOT_NODE_CLASS_NAME} ref={this.contextRef}>
          <IntlProvider key={locale} locale={locale} messages={messages}>
            <MainContainer
              session={session}
              apiUrl={apiUrl}
              scid={scid}
              highSchool={highSchool}
              userData={userData}
              collegeContact={collegeContact}
              applicationStatistics={applicationStatistics}
              deliveryType={deliveryType}
              isFavorite={isFavorite}
              isApplyingTo={isApplyingTo}
              highschoolCollegeInfo={highschoolCollegeInfo}
              origin={origin}
              toggleFavorite={
                this.canEditProspectiveApplication(isFavorite)
                  ? this.toggleFavorite
                  : null
              }
              navigateToNewCollegeProfile={this.navigateToNewCollegeProfile}
              isEditMode={isEditMode}
              editData={editData}
              sendEmail={this.sendEmail}
              fetchCollegeEvents={fetchCollegeEvents}
              fetchCounselorComments={fetchCounselorComments}
              visits={visits}
              clickTrackingEvents={clickTrackingEvents}
              permissions={permissions}
              featureFlags={featureFlags}
            />

            {/* The footer is only rendered if the feature flag is not enabled. Otherwise, it becomes visible for new college profiles. */}
            {Footer &&
            currentContextRef &&
            !this.state.userInfo?.featureFlags?.featurePersonalizedProfilesCollege ? (
              <Footer
                showEvents
                contextRef={currentContextRef}
                context="HUBS"
                uuid={uuid}
                scid={scid}
              />
            ) : null}
          </IntlProvider>
        </div>
      </HostContextProvider>
    );
  }
}
